/**
 * The SVG filter that bends what is behind the glass.
 *
 * HOW A DISPLACEMENT MAP WORKS, BECAUSE IT IS NOT OBVIOUS
 * ------------------------------------------------------
 * `feDisplacementMap` moves each pixel of the source by an amount read out of a
 * SECOND image. With `xChannelSelector="R"` and `yChannelSelector="G"`, a pixel
 * whose map colour is:
 *
 *   R=128 → no horizontal shift     R=0 → shift by −scale    R=255 → +scale
 *   G=128 → no vertical shift       G=0 → shift by −scale    G=255 → +scale
 *
 * So a map that is flat mid-grey (128,128) everywhere is a no-op, and real
 * glass is produced by pushing R and G away from 128 only near the EDGES —
 * which is where a lens actually bends light. The middle of a pane of glass
 * shows you the world unchanged; the rim is where it warps.
 *
 * THE TRICK THAT KEEPS THE TWO CHANNELS INDEPENDENT
 * -------------------------------------------------
 * The map is drawn as four edge gradients painted over a mid-grey base. Each
 * gradient varies ONE channel and holds the other at 128 — the left edge fades
 * from `rgba(0,128,128,1)` to `rgba(128,128,128,0)`, so as its alpha falls the
 * composite slides R back toward 128 while G, being 128 in both the gradient
 * and the base, never moves at all. Do it the naive way (opaque gradients that
 * overlap) and the vertical edges would clobber the horizontal ones' channel,
 * which shows up as the whole panel sliding diagonally instead of refracting.
 *
 * WHY IT IS INERT ALMOST EVERYWHERE
 * ---------------------------------
 * Only Chromium supports an SVG filter as a `backdrop-filter` input. This
 * element is rendered unconditionally — it costs nothing, it is `aria-hidden`,
 * and it has no visual presence of its own — while the CSS that USES it is
 * gated on `.iv-glass-refract`, applied at runtime by
 * `src/lib/glassRefraction.ts` only after the capability is tested.
 */

/** Displacement in pixels at the very edge. Small: glass refracts, it does not smear. */
const SCALE = 26;

/** How far in from each edge the bend reaches, as a fraction of the panel. */
const EDGE = '22%';

const MAP = `
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400">
  <defs>
    <linearGradient id="l" x1="0" y1="0" x2="${EDGE}" y2="0">
      <stop offset="0" stop-color="rgb(0,128,128)" stop-opacity="1"/>
      <stop offset="1" stop-color="rgb(128,128,128)" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="r" x1="100%" y1="0" x2="${100 - parseFloat(EDGE)}%" y2="0">
      <stop offset="0" stop-color="rgb(255,128,128)" stop-opacity="1"/>
      <stop offset="1" stop-color="rgb(128,128,128)" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="t" x1="0" y1="0" x2="0" y2="${EDGE}">
      <stop offset="0" stop-color="rgb(128,0,128)" stop-opacity="1"/>
      <stop offset="1" stop-color="rgb(128,128,128)" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="b" x1="0" y1="100%" x2="0" y2="${100 - parseFloat(EDGE)}%">
      <stop offset="0" stop-color="rgb(128,255,128)" stop-opacity="1"/>
      <stop offset="1" stop-color="rgb(128,128,128)" stop-opacity="0"/>
    </linearGradient>
  </defs>
  <rect width="400" height="400" fill="rgb(128,128,128)"/>
  <rect width="400" height="400" fill="url(#l)"/>
  <rect width="400" height="400" fill="url(#r)"/>
  <rect width="400" height="400" fill="url(#t)"/>
  <rect width="400" height="400" fill="url(#b)"/>
</svg>`.trim();

const MAP_HREF = `data:image/svg+xml;utf8,${encodeURIComponent(MAP)}`;

export function GlassRefractionFilter(): JSX.Element {
  return (
    <svg
      aria-hidden
      focusable="false"
      width="0"
      height="0"
      style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}
    >
      <defs>
        {/*
          `color-interpolation-filters="sRGB"` is REQUIRED. The SVG default is
          linearRGB, which silently re-maps the channel values the whole scheme
          above depends on — the map stops meaning what it says and the panel
          drifts instead of refracting.
        */}
        <filter
          id="iv-glass-displace"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          colorInterpolationFilters="sRGB"
        >
          <feImage href={MAP_HREF} preserveAspectRatio="none" result="map" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale={SCALE}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}
